import { Divider } from "@mui/material";
import {
	CompanyTestResultFieldType,
	ExpectationFieldType,
	Severity,
	TestingWorkflowInfoHierarchyNodeType,
	WorkflowStepType,
	WorkflowType,
} from "node-library/dist/constants";
import {
	FrontendBatchType,
	FrontendClient,
	FrontendCompanyResultFieldHierarchyNodeMappingPackage,
	FrontendCompanyTestCategory,
	FrontendCompanyTestResultField,
	FrontendCompanyTestResultUnits,
	FrontendCompanyWithAdditionalInfomation,
	FrontendOrganization,
	SysAdminCompanyTestingWorkflowWithBatchTypeIds,
} from "node-library/dist/portal";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ButtonTextLoadingAnimation } from "../../../../common-components/animation/ButtonLoadingAnimation";
import { GreenButton, TextButton } from "../../../../common-components/button";
import CountryProvinceSelector from "../../../../common-components/countryAndProvinceSelector";
import {
	CommonSwitch,
	CustomCheckbox,
	CustomDropdown,
	CustomTextInput,
} from "../../../../common-components/inputs";
import { ModalWithButtons } from "../../../../common-components/modals/modal";
import { RedErrorIcon } from "../../../../common-components/toast/errorIcons";
import { useSnackbar } from "../../../../common-components/toast/showToastMessage";
import { GreenTickIcon } from "../../../../common-components/toast/successIcons";
import { validateEmail } from "../../../../helpers/inputHelper";
import { sendPOSTRequest } from "../../../../requestHelper/sendRequest";
import { DeleteIcon } from "../../../../common-components/svgIcons";
import AddIcon from "@mui/icons-material/Add";
import { createUUID } from "node-library/dist/helpers/request_helper";
import CommonAutoCompleteDropdown from "../../../../common-components/commonAutoCompleteDropdown";
import { FrontendCompanyWorkflowSteps } from "node-library/dist/structures/frontendTestWorkflowStructureDefinitions";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	addOrEditWorkflowStep: (
		workflowStep: FrontendCompanyWorkflowSteps,
		isEdit: boolean,
		stepRootNodeInfo?: {
			stepRootNodeName?: string;
			stepRootNodeKey?: string;
			stepRootNodeType?: TestingWorkflowInfoHierarchyNodeType;
		},
	) => void;
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	loading: boolean;

	otherWorkflowSteps: Array<FrontendCompanyWorkflowSteps>;

	workflowStep?: FrontendCompanyWorkflowSteps;
}

export default function AddOrEditWorkflowStepModal(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const {
		company,
		open,
		setOpen,
		addOrEditWorkflowStep,
		workflowStep,
		otherWorkflowSteps,
		loading,
	} = props;

	const [stepName, setStepName] = useState<string>(
		workflowStep?.stepName || "",
	);
	const [stepDescription, setStepDescription] = useState<string>(
		workflowStep?.stepDescription || "",
	);

	const [stepType, setStepType] = useState<WorkflowStepType>(
		workflowStep?.stepType || WorkflowStepType.DATA,
	);

	const [isFirstStep, setIsFirstStep] = useState<boolean>(
		workflowStep?.isFirstStep || false,
	);

	const [parentStepId, setParentStepId] = useState<string | undefined>(
		workflowStep?.parentStepId || "",
	);

	const [rootHierarchyNodeName, setRootHierarchyNodeName] = useState<string>(
		workflowStep?.rootHierarchyNode?.nodeName || "",
	);
	const [rootHierarchyNodeKey, setRootHierarchyNodeKey] = useState<string>(
		workflowStep?.rootHierarchyNode?.nodeKey || "",
	);
	const [rootHierarchyNodeType, setRootHierarchyNodeType] =
		useState<TestingWorkflowInfoHierarchyNodeType>(
			workflowStep?.rootHierarchyNode?.nodeType ||
				TestingWorkflowInfoHierarchyNodeType.BATCH_INFO_GROUP,
		);

	//validations
	const [nameErrorMsg, setNameErrorMsg] = useState<string>("");

	const [rootHierarchyNodeNameErrorMsg, setRootHierarchyNodeNameErrorMsg] =
		useState<string>("");
	const [rootHierarchyNodeKeyErrorMsg, setRootHierarchyNodeKeyErrorMsg] =
		useState<string>("");

	function handleSubmit() {
		if (!stepName || stepName.trim().length == 0) {
			setNameErrorMsg(translation("MissingInformation"));
			return;
		}
		const shouldHaveRootNodeInfo: boolean =
			stepType !== WorkflowStepType.CONTAINER;
		// If stepType is not CONTAINER, rootHierarchyNodeName and rootHierarchyNodeKey must be provided.
		if (shouldHaveRootNodeInfo && !rootHierarchyNodeName) {
			setRootHierarchyNodeNameErrorMsg(translation("MissingInformation"));
			return;
		}
		if (shouldHaveRootNodeInfo && !rootHierarchyNodeKey) {
			setRootHierarchyNodeKeyErrorMsg(translation("MissingInformation"));
			return;
		}

		setNameErrorMsg("");
		setRootHierarchyNodeNameErrorMsg("");
		setRootHierarchyNodeKeyErrorMsg("");
		let workflowStepToSubmit: FrontendCompanyWorkflowSteps;
		const siblingSteps: Array<FrontendCompanyWorkflowSteps> =
			otherWorkflowSteps.filter((step) => {
				if (parentStepId) {
					return step.parentStepId === parentStepId;
				} else {
					//no parent
					return step.parentStepId ? false : true;
				}
			});
		const maxDisplayOrder: number =
			siblingSteps.length > 0
				? Math.max(...siblingSteps.map((f) => f.displayIndex))
				: -1;

		let fullIndexIncrement: number = 1;
		let immediateParentStep: FrontendCompanyWorkflowSteps | undefined =
			undefined;
		let parentIdToFind: string | undefined = parentStepId;
		while (parentIdToFind) {
			const parent: FrontendCompanyWorkflowSteps | undefined =
				otherWorkflowSteps.find((step) => step.stepId == parentIdToFind);
			if (!immediateParentStep && parent) {
				immediateParentStep = parent;
			}
			parentIdToFind = parent?.parentStepId;
			fullIndexIncrement *= 0.1;
		}
		// Now immediateParentStep holds the FrontendCompanyWorkflowSteps object of the immediate parent, if any.
		const maxFullIndex: number =
			siblingSteps.length > 0
				? Math.max(...siblingSteps.map((f) => f.fullIndex))
				: immediateParentStep?.fullIndex || -1;

		if (workflowStep) {
			workflowStepToSubmit = {
				...workflowStep,
				stepName,
				stepDescription,
				stepType,
				isFirstStep,
				displayIndex: maxDisplayOrder + 1,
				fullIndex: maxFullIndex + fullIndexIncrement,
				parentStepId,
			};
		} else {
			workflowStepToSubmit = {
				stepId: "",
				stepName,
				stepDescription,
				stepType,
				isFirstStep,
				displayIndex: maxDisplayOrder + 1,
				fullIndex: maxFullIndex + fullIndexIncrement,
				parentStepId,
				transitions: [],
				rootHierarchyNode: undefined,
			};
		}

		addOrEditWorkflowStep(
			workflowStepToSubmit,
			workflowStep ? true : false,
			shouldHaveRootNodeInfo
				? {
						stepRootNodeName: rootHierarchyNodeName,
						stepRootNodeKey: rootHierarchyNodeKey,
						stepRootNodeType: rootHierarchyNodeType,
					}
				: undefined,
		);
	}

	useEffect(() => {
		if (open) {
			setNameErrorMsg("");

			setStepName(workflowStep?.stepName || "");

			setStepDescription(workflowStep?.stepDescription || "");
			setStepType(workflowStep?.stepType || WorkflowStepType.DATA);

			setIsFirstStep(workflowStep?.isFirstStep || false);
			setParentStepId(workflowStep?.parentStepId || "");

			setRootHierarchyNodeName(workflowStep?.rootHierarchyNode?.nodeName || "");
			setRootHierarchyNodeKey(workflowStep?.rootHierarchyNode?.nodeKey || "");
			setRootHierarchyNodeType(
				workflowStep?.rootHierarchyNode?.nodeType ||
					TestingWorkflowInfoHierarchyNodeType.BATCH_INFO_GROUP,
			);
		}
	}, [open]);

	const fieldNameInput: React.ReactNode = (
		<CustomTextInput
			id="result-fieldname-input"
			key="result-fieldname-input"
			value={stepName}
			setValue={setStepName}
			label={translation("Name")}
			type="text"
			required
			errorMsg={nameErrorMsg}
			setErrorMsg={setNameErrorMsg}
		></CustomTextInput>
	);

	const fieldDescriptionInput: React.ReactNode = (
		<CustomTextInput
			id="result-description-input"
			key="result-description-input"
			value={stepDescription}
			setValue={setStepDescription}
			label={translation("Description")}
			type="text"
			isTextArea
		></CustomTextInput>
	);

	const fieldTypeInput: React.ReactNode = (
		<CustomDropdown
			id={"field-type-name"}
			label={translation("Type")}
			options={Object.values(WorkflowStepType).map((value) => {
				return { value: value, title: translation(value) };
			})}
			value={stepType}
			setValue={setStepType}
		></CustomDropdown>
	);

	const isFirstStepCheckbox: React.ReactNode = (
		<CustomCheckbox
			className={`checkbox-row`}
			label={translation("IsFirstStep")}
			labelClassName={"typography-body-02"}
			checked={isFirstStep}
			setChecked={() => {
				setIsFirstStep(!isFirstStep);
			}}
		/>
	);

	const otherStepOptions = otherWorkflowSteps.map((step) => {
		return { value: step.stepId, title: translation(step.stepName) };
	});

	otherStepOptions.push({
		title: `--${translation("None")}--`,
		value: "",
	});

	const parentStepIdDropdown: React.ReactNode = (
		<CustomDropdown
			id={"parent-step-dropdown"}
			label={translation("ParentStep")}
			options={otherStepOptions}
			value={parentStepId}
			setValue={(stepId: string) => {
				setParentStepId(stepId);
			}}
			displayEmpty
		></CustomDropdown>
	);

	const rootHierarchyNodeNameInput: React.ReactNode = (
		<CustomTextInput
			id="root-hierarchy-node-name-input"
			key="root-hierarchy-node-name-input"
			label={translation("RootNodeName")}
			type="text"
			required={stepType !== WorkflowStepType.CONTAINER}
			errorMsg={rootHierarchyNodeNameErrorMsg}
			setErrorMsg={setRootHierarchyNodeNameErrorMsg}
			value={rootHierarchyNodeName}
			setValue={setRootHierarchyNodeName}
		></CustomTextInput>
	);

	const rootHierarchyNodeKeyInput: React.ReactNode = (
		<CustomTextInput
			id="root-hierarchy-node-key-input"
			key="root-hierarchy-node-key-input"
			label={translation("RootNodeKey")}
			type="text"
			required={stepType !== WorkflowStepType.CONTAINER}
			errorMsg={rootHierarchyNodeKeyErrorMsg}
			setErrorMsg={setRootHierarchyNodeKeyErrorMsg}
			value={rootHierarchyNodeKey}
			setValue={setRootHierarchyNodeKey}
		></CustomTextInput>
	);
	const rootHierarchyNodeTypeDropdown: React.ReactNode = (
		<CustomDropdown
			id="root-hierarchy-node-type-dropdown"
			key="root-hierarchy-node-type-dropdown"
			label={translation("RootNodeType")}
			options={Object.values(TestingWorkflowInfoHierarchyNodeType).map(
				(value) => {
					return { value: value, title: translation(value) };
				},
			)}
			value={rootHierarchyNodeType}
			setValue={setRootHierarchyNodeType}
		></CustomDropdown>
	);

	const rootHierarchyNodeInput: React.ReactNode =
		stepType !== WorkflowStepType.CONTAINER ? (
			<div>
				{rootHierarchyNodeNameInput}
				{rootHierarchyNodeKeyInput}
				{rootHierarchyNodeTypeDropdown}
			</div>
		) : (
			<></>
		);

	const modalContent: React.ReactNode = (
		<div>
			{fieldNameInput}
			{fieldDescriptionInput}
			{fieldTypeInput}
			<div style={{ margin: "10px 0px" }}>{isFirstStepCheckbox}</div>
			{parentStepIdDropdown}
			{rootHierarchyNodeInput}
		</div>
	);

	const workflowStepModal: React.ReactNode = (
		<ModalWithButtons
			modalWidth={500}
			headerText={
				props.workflowStep
					? translation("EditWorkflowStep")
					: translation("CreateWorkflowStep")
			}
			// headerDescription={translation("ConfirmApprovalDescription")}
			open={open}
			setOpen={setOpen}
			buttons={[
				<GreenButton
					key="submit-client-change-button"
					id="submit-client-change-button"
					children={
						loading ? (
							<ButtonTextLoadingAnimation
								text={translation("Loading")}
							></ButtonTextLoadingAnimation>
						) : props.workflowStep ? (
							translation("Edit")
						) : (
							translation("Create")
						)
					}
					disabled={loading}
					onClick={handleSubmit}
				></GreenButton>,
			]}
		>
			{modalContent}
		</ModalWithButtons>
	);
	return workflowStepModal;
}
